import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
    X,
    ChevronDown,
    Scan,
    Image,
    ImageUp,
    FilePenLine,
    Wrench,
    Sparkle,
    Rocket,
    RefreshCcw,
    Flower
} from "lucide-react";

// ─── Palette (matches Home.jsx) ─────────────────────────────────────────────
// #0B3D2E deep forest | #145A43 mid forest | #2F8F6F sage green
// #D7C7A1 warm sand   | #0F0F0F near-black
// ─────────────────────────────────────────────────────────────────────────

const TOOLS = [
    {
        id: "scan-to-pdf",
        route: "iki-scan-to-pdf",
        icon: Scan,
        title: "Scan to PDF",
        description:
            "Turn your camera or drag-and-dropped photos into a clean, multi-page PDF in seconds. Works right in the browser, no app install needed.",
    },
    {
        id: "file-converter",
        route: "iki-file-converter",
        icon: RefreshCcw,
        title: "Format File Converter",
        description:
            "Convert files between common formats instantly, entirely on-device. Fast, private, and no upload to a server required.",
    },
    {
        id: "remove-bg",
        route: "iki-remove-bg",
        icon: Image,
        title: "Remove Background Image",
        description:
            "Easily remove backgrounds from images with high precision using our AI-powered isolation tool. Perfect for product photography and creative projects.",
    },
    {
        id: "signer-doc",
        route: "iki-signer-doc",
        icon: FilePenLine,
        title: "Document Signer",
        description:
            "Drop in a document, place your signature, and export a signed copy. No account needed, everything stays in your browser.",
        badge: { label: "New v1.0", tone: "blue", icon: Sparkle },
    },
    {
        id: "image-upscaler",
        route: "iki-image-upscaler",
        icon: ImageUp,
        title: "Image Upscaler",
        description:
            "Upscale low-res images into sharp, high-resolution versions using an on-device AI model. Great for old photos and small assets.",
        badge: { label: "Beta v0.8", tone: "green", icon: Wrench },
    },
    {
        id: "vector-converter",
        route: "iki-image-vector-converter",
        icon: Flower,
        title: "Image to Vector Converter",
        description:
            "Trace raster images into clean, scalable vector paths. Handy for logos, icons, and print-ready artwork.",
        badge: { label: "Beta v0.1", tone: "green", icon: Wrench },
    },
    {
        id: "vector-animation",
        route: "iki-vector-animation",
        icon: Flower,
        title: "Vector Animator",
        description:
            "Create smooth animations for your SVG graphics directly in the browser. No coding required.",
        badge: { label: "Beta v0.1", tone: "green", icon: Wrench },
    },
];

const badgeToneClasses = {
    green: "bg-green-700/30 text-[#D7C7A1]",
    blue: "bg-blue-700/30 text-[#D7C7A1]",
};

const ToolsModal = ({ open, onClose, onNavigate }) => {
    const [expandedId, setExpandedId] = useState(0); // "Remove Background Image" open by default, matches screenshot

    const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

    const handleUseTool = (route) => {
        onNavigate?.(route);
        onClose?.();
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 md:py-10"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                >
                    {/* Glassy backdrop */}
                    <motion.div
                        className="absolute inset-0 bg-[#0F0F0F]/60 backdrop-blur-xl"
                        onClick={onClose}
                    />

                    {/* Modal panel */}
                    <motion.div
                        role="dialog"
                        aria-modal="true"
                        aria-label="Iki's Free Tools"
                        className="
              relative w-full max-w-md md:max-w-lg
              max-h-[85vh] overflow-y-auto
              rounded-3xl
              border border-[#2F8F6F]/25
              bg-[#0F0F0F]/70
              backdrop-blur-2xl
              shadow-2xl shadow-black/50
              p-5 md:p-6
            "
                        initial={{ opacity: 0, y: 24, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 16, scale: 0.97 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between mb-5 px-1">
                            <h2 className="text-2xl md:text-3xl font-extrabold text-[#2F8F6F]">
                                Tools
                            </h2>
                            <button
                                onClick={onClose}
                                aria-label="Close tools modal"
                                className="
                  w-9 h-9 flex items-center justify-center
                  rounded-full
                  text-white/70 hover:text-white
                  hover:bg-white/10
                  transition-colors duration-200
                "
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Accordion list */}
                        <div className="flex flex-col gap-3">
                            {TOOLS.map((tool) => {
                                const Icon = tool.icon;
                                const isOpen = expandedId === tool.id;
                                const BadgeIcon = tool.badge?.icon;

                                return (
                                    <div
                                        key={tool.id}
                                        className={`
                      rounded-2xl overflow-hidden
                      border transition-colors duration-300
                      ${isOpen
                                                ? "border-[#2F8F6F]/50 bg-white/[0.04]"
                                                : "border-[#2F8F6F]/15 bg-white/[0.02] hover:bg-white/[0.05] hover:border-[#2F8F6F]/30"
                                            }
                    `}
                                    >
                                        <button
                                            onClick={() => toggle(tool.id)}
                                            className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
                                        >
                                            <span
                                                className="
                          shrink-0 w-9 h-9 rounded-xl
                          flex items-center justify-center
                          bg-[#145A43]/40 text-[#2F8F6F]
                        "
                                            >
                                                <Icon size={18} />
                                            </span>

                                            <span className="flex-1 flex items-center flex-wrap gap-2">
                                                <span className="text-white font-semibold text-sm md:text-base">
                                                    {tool.title}
                                                </span>
                                                {tool.badge && (
                                                    <span
                                                        className={`
                              inline-flex items-center gap-1
                              px-2 py-[2px] rounded-full
                              text-[9px] md:text-[10px] font-bold
                              ${badgeToneClasses[tool.badge.tone]}
                            `}
                                                    >
                                                        <BadgeIcon size={9} />
                                                        {tool.badge.label}
                                                    </span>
                                                )}
                                            </span>

                                            <ChevronDown
                                                size={18}
                                                className={`
                          shrink-0 text-white/60 transition-transform duration-300
                          ${isOpen ? "rotate-180 text-[#2F8F6F]" : ""}
                        `}
                                            />
                                        </button>

                                        <AnimatePresence initial={false}>
                                            {isOpen && (
                                                <motion.div
                                                    key="content"
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="px-4 pb-4 pt-1">
                                                        <p className="text-white/70 text-xs md:text-sm leading-relaxed mb-4">
                                                            {tool.description}
                                                        </p>
                                                        <button
                                                            onClick={() => handleUseTool(tool.route)}
                                                            className="
                                w-full md:w-auto
                                inline-flex items-center justify-center gap-2
                                px-5 py-2.5
                                rounded-full
                                bg-gradient-to-r from-[#2F8F6F] to-[#145A43]
                                text-white text-xs md:text-sm font-bold
                                hover:brightness-110 hover:scale-[1.02]
                                active:scale-[0.98]
                                transition-all duration-200
                                shadow-lg shadow-[#0B3D2E]/40
                              "
                                                        >
                                                            <Rocket size={14} />
                                                            Start Using Tool
                                                        </button>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default ToolsModal;